<template>
  <div
    v-if="municipio"
    class="simulador-modal-overlay"
    @click.self="$emit('close')"
  >
    <div class="simulador-modal">
      <div class="simulador-modal__header">
        <div>
          <h2>
            <i class="icofont-calculator"></i>
            Simulador
          </h2>

          <p>{{ municipio.municipio }} - {{ municipio.uf }}</p>
        </div>

        <button
          type="button"
          class="simulador-modal__close"
          @click="$emit('close')"
        >
          <i class="icofont-close"></i>
        </button>
      </div>

      <div class="simulador-modal__body">
        <div class="ranking-simulador">
          <div class="simulador-filtros">
            <div class="simulador-filtros__top">
              <div class="simulador-filtros__content">
                <h3>
                  <i class="icofont-sliders"></i>
                  Informe as equipes por classificação
                </h3>

                <div class="simulador-filtros__buttons">
                  <button
                    v-for="item in filtrosList"
                    :key="item"
                    type="button"
                    :class="{ active: filtros[item] }"
                    @click="toggleFiltro(item)"
                  >
                    {{ item }}
                  </button>
                </div>
              </div>

              <div class="simulador-ied">
                <label>IED do município</label>
                <strong>{{ municipio.ied || '-' }}</strong>
              </div>
            </div>
          </div>

          <div class="simulador-grid">
            <div
              v-for="(card, cardIndex) in cardsSimulador"
              :key="card.key"
              class="simulador-card"
              :class="card.key"
            >
              <div class="simulador-card__top"></div>

              <div class="simulador-card__body">
                <div class="simulador-card__title">
                  <h4>{{ card.label }}</h4>
                </div>

                <div
                  v-for="metric in card.metrics"
                  :key="metric.label"
                  class="simulador-metric"
                >
                  <div
                    class="simulador-metric__header"
                    @click="toggleExpand(cardIndex, metric.label)"
                  >
                    <div class="simulador-metric__header-left">
                      <div class="simulador-metric__info">
                        <span class="plus">+</span>

                        <span class="qtd">
                          {{ quantidadeMetric(cardIndex, metric.label) }}
                        </span>

                        <strong>{{ metric.label }}</strong>
                      </div>

                      <div class="simulador-metric__value">
                        =
                        {{
                          formatCurrency(
                            valorMetric(card, cardIndex, metric.label)
                          )
                        }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="expanded[`${cardIndex}-${metric.label}`]"
                    class="simulador-submetrics"
                  >
                    <div
                      v-for="sub in getSubMetricsVisiveis(metric.label)"
                      :key="sub.label"
                      class="simulador-submetric"
                    >
                      <div>
                        <span>+</span>

                        <input
                          type="number"
                          min="0"
                          :value="
                            getQuantidade(cardIndex, metric.label, sub.label)
                          "
                          @input="
                            setQuantidade(
                              cardIndex,
                              metric.label,
                              sub.label,
                              $event.target.value
                            )
                          "
                        />
                      </div>

                      <div class="simulador-submetric__text">
                        <span class="simulador-submetric__label">
                          {{ sub.label }}
                        </span>

                        <small class="simulador-submetric__price">
                          {{
                            formatCurrency(
                              getValorQualidade(
                                metric.label,
                                sub.label,
                                card.key
                              )
                            )
                          }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="simulador-card__footer">
                  <div class="simulador-total">
                    <span>=</span>
                    <strong>{{
                      formatCurrency(totalCard(card, cardIndex))
                    }}</strong>
                  </div>

                  <div v-if="card.key !== 'otimo'" class="simulador-perda">
                    <span>vs Ótimo</span>
                    <strong
                      >-
                      {{ formatCurrency(perdaCard(card, cardIndex)) }}</strong
                    >
                  </div>

                  <div v-else class="simulador-otimo">
                    <span>vs Ótimo</span>
                    <i class="icofont-trophy"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="simulador-resultado">
            <p>
              O município recebe atualmente
              <strong class="simulador-resultado__valor-atual">
                {{ formatCurrency(totalReceber) }}
              </strong>
              mensais, distribuídos entre os componentes
              <strong>Equidade</strong> ({{ formatCurrency(totalEquidade) }}),
              <strong>CVAT</strong> ({{ formatCurrency(totalCVAT) }}) e
              <strong>Qualidade</strong> ({{ formatCurrency(totalQualidade) }}).
              Com a melhoria da classificação de todas as equipes para
              <strong>Ótimo</strong>, o repasse mensal pode aumentar em mais
              <strong class="simulador-resultado__valor-ganho">
                {{ formatCurrency(totalPerda) }} </strong
              >.
            </p>
          </div>

          <div class="simulador-resumo-progress">
            <div class="simulador-resumo-progress__row">
              <div class="simulador-resumo-progress__metric is-recebendo">
                <div class="simulador-resumo-progress__content">
                  <span class="simulador-resumo-progress__label"
                    >Recebendo</span
                  >
                  <strong class="simulador-resumo-progress__value">
                    {{ formatCurrency(totalReceber) }}
                  </strong>
                </div>
              </div>

              <div class="simulador-resumo-progress__metric is-ganho">
                <div class="simulador-resumo-progress__content">
                  <span class="simulador-resumo-progress__label"
                    >Ganho potencial</span
                  >
                  <strong class="simulador-resumo-progress__value">
                    + {{ formatCurrency(totalPerda) }}
                  </strong>
                </div>
              </div>

              <div class="simulador-resumo-progress__metric is-maximo">
                <div class="simulador-resumo-progress__content">
                  <span class="simulador-resumo-progress__label"
                    >Máximo possível</span
                  >
                  <strong class="simulador-resumo-progress__value">
                    = {{ formatCurrency(totalMaximo) }}
                  </strong>
                </div>
              </div>
            </div>

            <div class="simulador-resumo-progress__bar" :title="progressTitle">
              <div class="simulador-resumo-progress__track"></div>
              <div
                class="simulador-resumo-progress__fill"
                :style="{ width: `${progressPercent}%` }"
              ></div>
              <div class="simulador-resumo-progress__caption">
                {{ progressPercent }}% do máximo
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  regras,
  getLabelRegra,
} from '@/pages/CofinanciamentoSimulacao/utils.js'

export default {
  name: 'ModalSimulador',

  props: {
    municipio: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      filtros: {
        CVAT: true,
        eSF: false,
        eSFR: false,
        eAP: false,
        eSB: false,
        eMulti: false,
        eCR: false,
        eAPP: false,
      },
      expanded: {},
      quantities: {},
      cards: [
        { key: 'regular', label: 'Regular' },
        { key: 'suficiente', label: 'Suficiente' },
        { key: 'bom', label: 'Bom' },
        { key: 'otimo', label: 'Ótimo' },
      ],
      metricsSchema: {
        CVAT: [
          { label: 'eSF' },
          { label: 'eSFR' },
          { label: 'eAP 20h' },
          { label: 'eAP 30h' },
        ],
        eSF: [{ label: 'eSF' }, { label: 'eSFR' }],
        eAP: [{ label: 'eAP 20h' }, { label: 'eAP 30h' }],
        eSB: [
          { label: 'eSB I - Comum' },
          { label: 'eSB I - Quil/Assent' },
          { label: 'eSB II - Comum' },
          { label: 'eSB II - Quil/Assent' },
        ],
        eMulti: [
          { label: 'Estratégica' },
          { label: 'Complementar' },
          { label: 'Ampliada' },
        ],
        eCR: [{ label: 'eCR I' }, { label: 'eCR II' }, { label: 'eCR III' }],
        eAPP: [
          { label: 'Essencial 20h' },
          { label: 'Essencial 30h' },
          { label: 'Ampliada 20h' },
          { label: 'Ampliada 30h' },
        ],
      },
      equityValues: {
        eSF: [18000, 16000, 14000, 12000],
        eAP30h: [10800, 9600, 8400, 7200],
        eAP20h: [7200, 6400, 5600, 4800],
      },
    }
  },

  computed: {
    filtrosList() {
      return ['eSF', 'eSFR', 'eAP', 'eSB', 'eMulti', 'eCR', 'eAPP']
    },

    cardsSimulador() {
      return this.cards.map((card) => {
        const metrics = [
          'CVAT',
          ...this.filtrosList.filter((label) => this.filtros[label]),
        ]
          .map((label) => ({ label }))
          .filter((metric) => this.getSubMetricsVisiveis(metric.label).length)

        return {
          ...card,
          metrics,
        }
      })
    },

    totalCVAT() {
      return this.calcularTotais().cvat
    },

    totalQualidade() {
      return this.calcularTotais().qualidade
    },

    totalPerda() {
      return this.calcularTotais().perda
    },

    estrato() {
      return Number(this.municipio?.ied || 1)
    },

    totalEquidade() {
      return this.calcularTotais().equidade
    },

    totalReceber() {
      return this.totalCVAT + this.totalQualidade + this.totalEquidade
    },

    totalMaximo() {
      return this.totalReceber + this.totalPerda
    },

    progressPercent() {
      if (!this.totalMaximo) return 0

      return Math.max(
        0,
        Math.min(100, Math.round((this.totalReceber / this.totalMaximo) * 100))
      )
    },

    progressTitle() {
      return `${this.formatCurrency(
        this.totalReceber
      )} de ${this.formatCurrency(this.totalMaximo)}`
    },
  },

  watch: {
    municipio: {
      immediate: true,
      handler() {
        this.preencherQuantidades()
      },
    },
  },

  methods: {
    /**
     * Ativa ou desativa um filtro.
     *
     * @param {string} filtro Filtro selecionado.
     * @returns {void}
     */
    toggleFiltro(filtro) {
      this.$set(this.filtros, filtro, !this.filtros[filtro])
    },

    /**
     * Alterna a expansão da métrica.
     *
     * @param {number} cardIndex Índice do card.
     * @param {string} metricLabel Nome da métrica.
     * @returns {void}
     */
    toggleExpand(cardIndex, metricLabel) {
      const key = `${cardIndex}-${metricLabel}`

      this.$set(this.expanded, key, !this.expanded[key])
    },

    /**
     * Preenche o simulador com os dados atuais do município.
     *
     * Quando não sabemos o subtipo da equipe, preenche sempre a primeira opção.
     *
     * @returns {void}
     */
    preencherQuantidades() {
      this.quantities = {}

      this.cards.forEach((card, cardIndex) => {
        const classificacao = this.municipio?.[card.key] || {}

        this.preencherPrimeiraEquipe(
          cardIndex,
          'CVAT',
          'eSF',
          classificacao.esf_cvat
        )
        this.preencherPrimeiraEquipe(
          cardIndex,
          'CVAT',
          'eAP 20h',
          classificacao.eap_cvat
        )

        this.preencherPrimeiraEquipe(
          cardIndex,
          'eSF',
          'eSF',
          classificacao.esf_qual
        )
        this.preencherPrimeiraEquipe(
          cardIndex,
          'eAP',
          'eAP 20h',
          classificacao.eap_qual
        )
        this.preencherPrimeiraEquipe(
          cardIndex,
          'eSB',
          'eSB I - Comum',
          classificacao.esb_qual
        )
        this.preencherPrimeiraEquipe(
          cardIndex,
          'eMulti',
          'Estratégica',
          classificacao.emulti_qual
        )
      })

      this.ativarFiltrosComDados()
    },

    /**
     * Preenche uma subequipe específica.
     *
     * @param {number} cardIndex Índice do card.
     * @param {string} metricLabel Nome da métrica.
     * @param {string} subLabel Nome da subequipe.
     * @param {number|string|null} value Valor recebido.
     * @returns {void}
     */
    preencherPrimeiraEquipe(cardIndex, metricLabel, subLabel, value) {
      const quantidade = Number(value || 0)

      if (quantidade <= 0) return

      const key = this.getQuantidadeKey(cardIndex, metricLabel, subLabel)

      this.$set(this.quantities, key, quantidade)
    },

    /**
     * Ativa automaticamente os filtros que possuem dados.
     *
     * @returns {void}
     */
    ativarFiltrosComDados() {
      const possui = {
        eSF: false,
        eAP: false,
        eSB: false,
        eMulti: false,
      }

      this.cards.forEach((card) => {
        const classificacao = this.municipio?.[card.key] || {}

        if (
          Number(classificacao.esf_cvat || 0) > 0 ||
          Number(classificacao.esf_qual || 0) > 0
        ) {
          possui.eSF = true
        }

        if (
          Number(classificacao.eap_cvat || 0) > 0 ||
          Number(classificacao.eap_qual || 0) > 0
        ) {
          possui.eAP = true
        }

        if (Number(classificacao.esb_qual || 0) > 0) {
          possui.eSB = true
        }

        if (Number(classificacao.emulti_qual || 0) > 0) {
          possui.eMulti = true
        }
      })

      Object.keys(possui).forEach((key) => {
        this.$set(this.filtros, key, possui[key])
      })
    },

    /**
     * Retorna as submétricas da métrica.
     *
     * @param {string} metricLabel Nome da métrica.
     * @returns {Array}
     */
    getSubMetrics(metricLabel) {
      return this.metricsSchema[metricLabel] || []
    },

    /**
     * Retorna as submétricas visíveis.
     *
     * @param {string} metricLabel Nome da métrica.
     * @returns {Array}
     */
    getSubMetricsVisiveis(metricLabel) {
      return this.getSubMetrics(metricLabel).filter((sub) => {
        if (sub.label === 'eSF' && !this.filtros.eSF) return false
        if (sub.label === 'eSFR' && !this.filtros.eSFR) return false
        if (sub.label.includes('eAP') && !this.filtros.eAP) return false
        if (sub.label.includes('eSB') && !this.filtros.eSB) return false
        if (
          ['Estratégica', 'Complementar', 'Ampliada'].includes(sub.label) &&
          !this.filtros.eMulti
        )
          return false
        if (sub.label.includes('eCR') && !this.filtros.eCR) return false
        if (
          [
            'Essencial 20h',
            'Essencial 30h',
            'Ampliada 20h',
            'Ampliada 30h',
          ].includes(sub.label) &&
          !this.filtros.eAPP
        )
          return false

        return true
      })
    },

    /**
     * Monta a chave da quantidade.
     *
     * @param {number} cardIndex Índice do card.
     * @param {string} metricLabel Nome da métrica.
     * @param {string} subLabel Nome da subequipe.
     * @returns {string}
     */
    getQuantidadeKey(cardIndex, metricLabel, subLabel) {
      return `${cardIndex}-${metricLabel}-${subLabel}`
    },

    /**
     * Retorna a quantidade.
     *
     * @param {number} cardIndex Índice do card.
     * @param {string} metricLabel Nome da métrica.
     * @param {string} subLabel Nome da subequipe.
     * @returns {number|string}
     */
    getQuantidade(cardIndex, metricLabel, subLabel) {
      const key = this.getQuantidadeKey(cardIndex, metricLabel, subLabel)

      return this.quantities[key] === undefined ? 0 : this.quantities[key]
    },

    /**
     * Define a quantidade.
     *
     * @param {number} cardIndex Índice do card.
     * @param {string} metricLabel Nome da métrica.
     * @param {string} subLabel Nome da subequipe.
     * @param {string|number} value Valor informado.
     * @returns {void}
     */
    setQuantidade(cardIndex, metricLabel, subLabel, value) {
      const quantidade = value === '' ? '' : Math.max(0, Number(value || 0))
      const key = this.getQuantidadeKey(cardIndex, metricLabel, subLabel)

      this.$set(this.quantities, key, quantidade)
    },

    /**
     * Soma a quantidade da métrica no card.
     *
     * @param {number} cardIndex Índice do card.
     * @param {string} metricLabel Nome da métrica.
     * @returns {number}
     */
    quantidadeMetric(cardIndex, metricLabel) {
      return this.getSubMetricsVisiveis(metricLabel).reduce((total, sub) => {
        return (
          total +
          Number(this.getQuantidade(cardIndex, metricLabel, sub.label) || 0)
        )
      }, 0)
    },

    /**
     * Retorna o valor da regra.
     *
     * @param {string} metricLabel Nome da métrica.
     * @param {string} subLabel Nome da subequipe.
     * @param {string} cardKey Classificação.
     * @returns {number}
     */
    getValorQualidade(metricLabel, subLabel, cardKey) {
      const classificacoes = {
        regular: 'Regular',
        suficiente: 'Suficiente',
        bom: 'Bom',
        otimo: 'Ótimo',
      }

      const listaRegras =
        metricLabel === 'CVAT'
          ? regras.vinculo_e_acompanhamento
          : regras.qualidade

      const regra = listaRegras.find((item) => {
        return item.label === getLabelRegra(subLabel)
      })

      const classificacao = regra?.classificacao?.find((item) => {
        return item.tipo === classificacoes[cardKey]
      })

      return Number(classificacao?.valor || 0)
    },

    /**
     * Calcula o valor da métrica no card.
     *
     * @param {Object} card Card atual.
     * @param {number} cardIndex Índice do card.
     * @param {string} metricLabel Nome da métrica.
     * @returns {number}
     */
    valorMetric(card, cardIndex, metricLabel) {
      return this.getSubMetricsVisiveis(metricLabel).reduce((total, sub) => {
        const quantidade = Number(
          this.getQuantidade(cardIndex, metricLabel, sub.label) || 0
        )
        const valor = this.getValorQualidade(metricLabel, sub.label, card.key)

        return total + quantidade * valor
      }, 0)
    },

    /**
     * Calcula o total do card.
     *
     * @param {Object} card Card atual.
     * @param {number} cardIndex Índice do card.
     * @returns {number}
     */
    totalCard(card, cardIndex) {
      return card.metrics.reduce((total, metric) => {
        return total + this.valorMetric(card, cardIndex, metric.label)
      }, 0)
    },

    /**
     * Calcula a perda em relação ao ótimo.
     *
     * @param {Object} card Card atual.
     * @param {number} cardIndex Índice do card.
     * @returns {number}
     */
    perdaCard(card, cardIndex) {
      if (card.key === 'otimo') return 0

      return card.metrics.reduce((total, metric) => {
        return (
          total +
          this.getSubMetricsVisiveis(metric.label).reduce((subtotal, sub) => {
            const quantidade = Number(
              this.getQuantidade(cardIndex, metric.label, sub.label) || 0
            )
            const valorAtual = this.getValorQualidade(
              metric.label,
              sub.label,
              card.key
            )
            const valorOtimo = this.getValorQualidade(
              metric.label,
              sub.label,
              'otimo'
            )

            return subtotal + quantidade * (valorOtimo - valorAtual)
          }, 0)
        )
      }, 0)
    },

    /**
     * Calcula os totais gerais do simulador.
     *
     * @returns {Object}
     */
    calcularTotais() {
      let cvat = 0
      let qualidade = 0
      let equidade = 0
      let perda = 0

      this.cardsSimulador.forEach((card, cardIndex) => {
        card.metrics.forEach((metric) => {
          this.getSubMetricsVisiveis(metric.label).forEach((sub) => {
            const quantidade = Number(
              this.getQuantidade(cardIndex, metric.label, sub.label) || 0
            )

            const valorAtual =
              quantidade *
              this.getValorQualidade(metric.label, sub.label, card.key)

            const valorMaximo =
              quantidade *
              this.getValorQualidade(metric.label, sub.label, 'otimo')

            if (metric.label === 'CVAT') {
              cvat += valorAtual
            } else {
              qualidade += valorAtual
            }
            if (card.key !== 'otimo') {
              perda += valorMaximo - valorAtual
            }
          })
        })
      })

      equidade = this.calcularEquidade()

      return { cvat, qualidade, equidade, perda }
    },

    /**
     * Formata valor em moeda.
     *
     * @param {number|string} value Valor informado.
     * @returns {string}
     */
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(value || 0))
    },

    /**
     * Soma o maior valor encontrado para cada submétrica,
     * evitando duplicidade entre CVAT e Qualidade.
     *
     * @param {Array} subLabels Labels das subequipes.
     * @returns {number}
     */
    somarMaximoPorSubLabel(subLabels) {
      const metricLabels = Object.keys(this.metricsSchema || {})

      return this.cardsSimulador.reduce((acc, _card, cardIndex) => {
        const totalCard = subLabels.reduce((sum, subLabel) => {
          const maxNoCard = metricLabels.reduce((max, metricLabel) => {
            const key = this.getQuantidadeKey(cardIndex, metricLabel, subLabel)
            const val = Number(this.quantities?.[key] || 0)

            return val > max ? val : max
          }, 0)

          return sum + maxNoCard
        }, 0)

        return acc + totalCard
      }, 0)
    },

    /**
     * Calcula o valor total da equidade.
     *
     * @returns {number}
     */
    calcularEquidade() {
      const estrato = this.estrato

      const valorESF = this.equityValues.eSF[estrato - 1]
      const valorEAP30h = this.equityValues.eAP30h[estrato - 1]
      const valorEAP20h = this.equityValues.eAP20h[estrato - 1]

      const totalESF = this.filtros.eSF
        ? this.somarMaximoPorSubLabel(['eSF'])
        : 0

      const totalEAP30h = this.filtros.eAP
        ? this.somarMaximoPorSubLabel(['eAP 30h'])
        : 0

      const totalEAP20h = this.filtros.eAP
        ? this.somarMaximoPorSubLabel(['eAP 20h'])
        : 0

      return (
        totalESF * valorESF +
        totalEAP30h * valorEAP30h +
        totalEAP20h * valorEAP20h
      )
    },
  },
}
</script>

<style scoped>
.ranking-simulador {
  margin-top: 24px;
}

.simulador-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(15, 23, 42, 0.58);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 18px;
  overflow-y: auto;
}

.simulador-modal {
  width: min(1280px, 100%);
  background: #f8fafc;
  border-radius: 24px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.35);
  overflow: hidden;
}

.simulador-modal__header {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 22px 26px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.simulador-modal__header h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
}

.simulador-modal__header p {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
}

.simulador-modal__close {
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  font-size: 18px;
}

.simulador-modal__close:hover {
  background: #fee2e2;
  color: #dc2626;
}

.simulador-modal__body {
  padding: 24px;
}

.ranking-simulador {
  margin-top: 0;
}

@media (max-width: 768px) {
  .simulador-modal-overlay {
    padding: 12px;
  }

  .simulador-modal {
    border-radius: 18px;
  }

  .simulador-modal__header {
    padding: 18px;
  }

  .simulador-modal__body {
    padding: 16px;
  }
}

.simulador-filtros {
  background: #fff;
  border-radius: 24px;
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
}

.simulador-filtros__top {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.simulador-filtros__content {
  flex: 1;
  min-width: 0;
}

.simulador-filtros h3 {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
  margin: 0 0 14px;
}

.simulador-filtros__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.simulador-filtros button {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #94a3b8;
  border-radius: 12px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 900;
  width: auto;
  box-shadow: none;
  text-transform: initial;
  cursor: pointer;
}

.simulador-filtros button.active {
  background: #3f84b6;
  border-color: #3f84b6;
  color: #ffffff;
}

.simulador-filtros button:hover {
  border-color: #3f84b6;
  color: #3f84b6;
}

.simulador-filtros button.active:hover {
  color: #ffffff;
}

.simulador-metric__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.simulador-metric__header-left {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
}

.qtd {
  width: 48px;
  height: 32px;
  border-radius: 10px;
  background: #eff6ff;
  color: #3f84b6;
  border: 1px solid #dbeafe;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
}

.simulador-metric__value {
  font-size: 15px;
  font-weight: 900;
  color: #0f172a;
  white-space: nowrap;
}

.simulador-submetrics {
  margin-top: 12px;
  margin-left: 22px;
  padding-left: 14px;
  border-left: 2px solid #e2e8f0;
}

.simulador-submetric {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: 10px;
  margin-bottom: 12px;
  padding: 4px 0;
}

.simulador-submetric > div:first-child {
  display: grid;
  grid-template-columns: 14px 44px;
  align-items: center;
  gap: 10px;
}

.simulador-submetric__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}

.simulador-submetric__label {
  font-size: 12px;
  font-weight: 800;
  color: #64748b;
  white-space: nowrap;
}

.simulador-submetric__price {
  font-size: 11px;
  font-weight: 900;
  color: #94a3b8;
  margin-top: 2px;
  white-space: nowrap;
}

.simulador-submetric input {
  width: 44px !important;
  min-width: 44px !important;
  max-width: 44px !important;
  height: 28px !important;
  border: 1px solid #e2e8f0 !important;
  background: #f8fafc !important;
  border-radius: 7px !important;
  padding: 4px !important;
  text-align: center !important;
  font-size: 11px !important;
  font-weight: 900 !important;
  color: #64748b !important;
  box-shadow: none !important;
  margin: 0 !important;
}

@media (max-width: 768px) {
  .simulador-filtros__top {
    flex-direction: column;
  }

  .simulador-metric__header-left {
    align-items: flex-start;
    flex-direction: column;
  }

  .simulador-metric__value {
    margin-left: 38px;
  }
}

.simulador-ied {
  min-width: 160px;
  background: #f8fafc;
  border-radius: 18px;
  padding: 16px;
}

.simulador-ied label {
  display: block;
  font-size: 12px;
  font-weight: 900;
  color: #64748b;
  margin-bottom: 6px;
}

.simulador-ied strong {
  font-size: 22px;
  color: #0f172a;
}

.simulador-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 18px;
}

.simulador-card {
  background: #fff;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid #f1f5f9;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.simulador-card__top {
  height: 12px;
}

.simulador-card.regular .simulador-card__top {
  background: #ce7000;
}

.simulador-card.suficiente .simulador-card__top {
  background: #d1a100;
}

.simulador-card.bom .simulador-card__top {
  background: #00a972;
}

.simulador-card.otimo .simulador-card__top {
  background: #3f84b6;
}

.simulador-card__body {
  padding: 20px;
}

.simulador-card__title {
  text-align: center;
  margin-bottom: 16px;
}

.simulador-card__title h4 {
  margin: 0;
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
}

.simulador-metric {
  border-bottom: 1px solid #f1f5f9;
  padding: 10px 0;
}

.simulador-metric__info {
  display: grid;
  grid-template-columns: 14px 56px 1fr;
  align-items: center;
  gap: 10px;
}

.simulador-metric__info strong {
  font-size: 12px;
  color: #64748b;
}

.plus {
  color: #cbd5e1;
  font-weight: 900;
  font-size: 18px;
}

.simulador-metric input {
  width: 56px;
  height: 30px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 8px;
  padding: 4px;
  text-align: center;
  font-size: 12px;
  font-weight: 900;
  color: #64748b;
}

.simulador-card__footer {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 2px dashed #f1f5f9;
}

.simulador-total,
.simulador-perda,
.simulador-otimo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 16px;
  padding: 12px;
  margin-top: 10px;
  font-weight: 900;
  font-size: 13px;
}

.simulador-total {
  background: #f8fafc;
  color: #0f172a;
}

.simulador-perda {
  background: #fef2f2;
  color: #dc2626;
}

.simulador-otimo {
  background: #f0fdf4;
  color: #15803d;
}

.simulador-resultado {
  margin-top: 24px;
  background: #fff;
  border-left: 8px solid #3f84b6;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.simulador-resultado p {
  margin: 0;
  font-size: 16px;
  color: #334155;
  line-height: 1.6;
}

.simulador-resultado__valor-atual {
  color: #16a34a;
}

.simulador-resultado__valor-ganho {
  color: #f59e0b;
}

.simulador-resumo-progress {
  margin-top: 22px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid #f1f5f9;
  border-radius: 24px;
  padding: 16px 18px 18px;
}

.simulador-resumo-progress__row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 18px;
  align-items: start;
}

.simulador-resumo-progress__metric {
  display: flex;
  align-items: center;
  min-width: 0;
}

.simulador-resumo-progress__content {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.simulador-resumo-progress__label {
  display: block;
  font-size: 12px;
  font-weight: 900;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.simulador-resumo-progress__value {
  font-size: 28px;
  font-weight: 900;
  margin: 0;
  line-height: 1.1;
}

.simulador-resumo-progress__metric.is-recebendo
  .simulador-resumo-progress__value {
  color: #16a34a;
}

.simulador-resumo-progress__metric.is-ganho .simulador-resumo-progress__value {
  color: #f59e0b;
}

.simulador-resumo-progress__metric.is-maximo .simulador-resumo-progress__value {
  color: #354abb;
}

.simulador-resumo-progress__bar {
  position: relative;
  margin-top: 14px;
  padding-top: 12px;
}

.simulador-resumo-progress__track {
  height: 10px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.simulador-resumo-progress__fill {
  position: absolute;
  left: 0;
  top: 12px;
  height: 10px;
  background: linear-gradient(90deg, #16a34a 0%, #f59e0b 55%, #354abb 100%);
  border-radius: 999px;
  transition: width 180ms ease;
}

.simulador-resumo-progress__caption {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 900;
  color: #64748b;
  text-align: right;
}

@media (max-width: 768px) {
  .simulador-filtros__top {
    flex-direction: column;
  }

  .simulador-estrato {
    width: 100%;
    min-width: 100%;
  }

  .simulador-resumo-progress__row {
    grid-template-columns: 1fr;
  }
}
</style>
